/**
 * NOTE: Autocomplete behavior reference (Global Search V2 / Tax Treaty V2)
 *
 * There is no hard "minimum 3 characters" validation at endpoint level,
 * but backend logic suppresses many short queries.
 *
 * Endpoints:
 * - /api/v2/global-search/tax-transparency/autocomplete
 * - /api/v2/tax-treaties/autocomplete
 *
 * Controller flow:
 * - SearchV2Controller -> taxTransparencyAutocomplete(q)
 * - TaxTreatyV2Controller -> autocompleteV2(q)
 *
 * Backend rule:
 * - SearchV2Service.MIN_AUTOCOMPLETE_QUERY_LENGTH_V2 = 3
 * - If query length < 3, canonical lookup is skipped unless:
 *   1) explicit expansion happened (example: "tp" -> "transfer pricing"), or
 *   2) query starts with a digit.
 *
 * UI behavior:
 * - useAutocompleteRequestKey uses minLength: 2, but this value is not used to
 *   gate API calls in the current flow (only computed in the hook).
 * - API calls are made for any non-empty trimmed query by key consumers.
 *
 * Local runtime observations (localhost backend):
 * - q=t   -> 0 suggestions
 * - q=tp  -> suggestions present
 * - q=tax -> suggestions present
 * - q=1   -> suggestions present
 */
export const ABBREVIATIONS_LENGTH_LE_3_FULL_FORMS = {
  aaoifi:
    'accounting and auditing organization for islamic financial institutions',
  act: 'adjusted covered taxes',
  actt: 'additional current top-up tax',
  aed: 'united arab emirates dirham',
  aeo: 'authorised economic operator',
  aeoi: 'automatic exchange of information',
  afas: 'acceptable financial accounting standard',
  afs: 'audited financial statements',
  alp: "arm\'s length principle",
  aml: 'anti-money laundering',
  apa: 'advance pricing agreement',
  beps: 'base erosion and profit shifting',
  bhr: 'bahrain',
  cbc: 'country-by-country',
  cbcr: 'country-by-country reporting',
  cca: 'cost contribution arrangement',
  cd: 'cabinet decision',
  ce: 'constituent entity',
  cfc: 'controlled foreign company',
  cis: 'collective investment scheme',
  cit: 'corporate income tax',
  cpm: 'cost plus method',
  crs: 'common reporting standard',
  ct: 'corporate tax',
  cup: 'comparable uncontrolled price method',
  ddfe: 'domestic designated filing entity',
  decision: 'decision no.',
  dle: 'designated local entity',
  dmtt: 'domestic minimum top-up tax',
  dpe: 'domestic permanent establishment',
  dtaa: 'double taxation avoidance agreement',
  dta: 'deferred tax asset',
  dz: 'designated zone',
  ebitda: 'earnings before interest, tax, depreciation and amortisation',
  emc: 'effectively managed and controlled',
  epw: 'externally provided workers',
  esop: 'employee stock ownership plan',
  esr: 'economic substance regulations',
  etr: 'effective tax rate',
  eur: 'euro',
  fanil: 'financial accounting net income or loss',
  fatca: 'foreign account tax compliance act',
  fce: 'filing constituent entity',
  fd: 'federal decree',
  'fdl 60': 'federal decree-law 60 of 2023',
  fdl: 'federal decree-law',
  fis: 'fees for included services',
  foa: 'force of attraction',
  fpe: 'foreign permanent establishment',
  fs: 'financial statements',
  fta: 'federal tax authority',
  fte: 'full time employees',
  fts: 'fees for technical services',
  fy: 'financial year',
  fz: 'free zone',
  fzp: 'free zone person',
  gaap: 'generally accepted accounting principles',
  gaar: 'general anti-avoidance rule',
  gazt: 'general authority of zakat and tax',
  gcc: 'gulf cooperation council',
  gir: 'global anti-base erosion rules information return',
  globe: 'global anti-base erosion rules',
  gta: 'general tax authority',
  ho: 'head office',
  ifrs: 'international financial reporting standards',
  iir: 'income inclusion rule',
  ipe: 'intermediate parent entity',
  ip: 'intellectual property',
  ipr: 'intellectual property rights',
  isesco: 'islamic world educational scientific and cultural organization',
  itc: 'input tax credit',
  jv: 'joint venture',
  ksa: 'kingdom of saudi arabia',
  kwt: 'kuwait',
  kyc: 'know your customer',
  lce: 'liable constituent entity',
  lob: 'limitation on benefits',
  map: 'mutual agreement procedure',
  md: 'ministerial decision',
  mfn: 'most favoured nation',
  mli: 'multilateral instrument',
  mne: 'multinational enterprise',
  moce: 'minority-owned constituent entity',
  mof: 'ministry of finance',
  mope: 'minority-owned parent entity',
  mttc: 'marketable transferable tax credit',
  nbr: 'national bureau for revenue',
  nbt: 'national bureau for taxation',
  npo: 'non-profit organisation',
  oecd: 'organisation for economic co-operation and development',
  omn: 'oman',
  omv: 'open market value',
  ota: 'oman tax authority',
  otc: 'output tax',
  pe: 'permanent establishment',
  pit: 'personal income tax',
  'p&l': 'profit & loss',
  poc: 'proof of concept',
  poem: 'place of effective management',
  pope: 'partially-owned parent entity',
  pos: 'place of supply',
  ppt: 'principal purpose test',
  psm: 'profit split method',
  qat: 'qatar',
  qcaa: 'qualifying competent authority agreement',
  qdmtt: 'qualified domestic minimum top-up tax',
  qe: 'qualifying entity',
  qfzp: 'qualifying free zone person',
  qfz: 'qualifying free zone',
  qif: 'qualifying investment fund',
  qip: 'qualifying infrastructure project',
  qoi: 'qualified ownership interest',
  qpbe: 'qualifying public benefit entity',
  qrtc: 'qualified refundable tax credit',
  rcm: 'reverse charge mechanism',
  'r&d': 'research and development',
  reit: 'real estate investment trust',
  reiv: 'real estate investment vehicle',
  rfy: 'reporting fiscal year',
  rpm: 'resale price method',
  sbie: 'substance-based income exclusion',
  sbr: 'small business relief',
  setr: 'simplified effective tax rate',
  sme: 'small and medium-sized enterprise',
  sttr: 'subject to tax rule',
  'tax holiday': '0% (zero percent) on qualifying income',
  tdm: 'taxable distribution method',
  tdrc: 'tax disputes resolution committee',
  tds: 'tax deduction at source',
  tdtaa: 'total deferred tax adjustment amount',
  tin: 'tax identification number',
  tnmm: 'transactional net margin method',
  togc: 'transfer of business as a going concern',
  tpl: 'tax procedures law',
  tp: 'transfer pricing',
  trc: 'tax residency certificate',
  trn: 'tax registration number',
  tsf: 'technical service fee',
  ttp: 'top-up tax percentage',
  uae: 'united arab emirates',
  upe: 'ultimate parent entity',
  utpr: 'undertaxed profits rule',
  vat: 'value added tax',
  wht: 'withholding tax',
  zatca: 'zakat, tax and customs authority',
}
